/**
 * Synthia Engine 配置验证器 - 简化版
 * 提供基本的配置验证功能
 */

import type { SynthiaConfig, ConfigValidationResult } from './config';

/**
 * 简化的配置验证器类
 */
export class ConfigValidator {
  private errors: string[] = [];
  private warnings: string[] = [];
  private suggestions: string[] = [];

  /**
   * 验证配置
   */
  validate(config: SynthiaConfig): ConfigValidationResult {
    this.reset();

    // 验证基本配置
    this.validateBasicConfig(config);

    // 验证插件配置
    this.validatePluginConfig(config.plugins);

    // 验证日志配置
    this.validateLoggerConfig(config.logger);

    return {
      valid: this.errors.length === 0,
      errors: [...this.errors],
      warnings: [...this.warnings],
      suggestions: [...this.suggestions],
    };
  }

  /**
   * 验证基本配置
   */
  private validateBasicConfig(config: SynthiaConfig): void {
    if (!config.name) {
      this.errors.push('项目名称 (name) 是必需的');
    }

    if (!config.version) {
      this.errors.push('项目版本 (version) 是必需的');
    }

    if (config.name && typeof config.name !== 'string') {
      this.errors.push('项目名称 (name) 必须是字符串');
    }

    if (config.version && typeof config.version !== 'string') {
      this.errors.push('项目版本 (version) 必须是字符串');
    }

    if (config.description && typeof config.description !== 'string') {
      this.warnings.push('项目描述 (description) 应该是字符串');
    }
  }

  /**
   * 验证插件配置
   */
  private validatePluginConfig(plugins?: any): void {
    if (!plugins) return;

    if (!Array.isArray(plugins)) {
      this.errors.push('插件配置 (plugins) 必须是数组');
      return;
    }

    for (let i = 0; i < plugins.length; i++) {
      const plugin = plugins[i];

      if (typeof plugin === 'function') {
        // 函数格式的插件配置
        // 函数式插件不需要额外验证
        continue;
      } else if (typeof plugin === 'object' && plugin !== null) {
        // 对象格式的插件配置
        if (!plugin.name) {
          this.errors.push(`插件配置 [${i}] 缺少 name 属性`);
        } else if (typeof plugin.name !== 'string') {
          this.errors.push(`插件配置 [${i}] 的 name 必须是字符串`);
        }

        if (!plugin.plugin) {
          this.errors.push(`插件配置 [${i}] 缺少 plugin 属性`);
        } else if (typeof plugin.plugin !== 'function') {
          this.errors.push(`插件配置 [${i}] 的 plugin 必须是函数`);
        }
      } else {
        this.errors.push(
          `插件配置 [${i}] 必须是函数或对象格式 { name: string, plugin: function }`
        );
      }
    }
  }

  /**
   * 验证日志配置
   */
  private validateLoggerConfig(logger?: any): void {
    if (!logger) return;

    if (typeof logger !== 'object') {
      this.errors.push('日志配置 (logger) 必须是对象');
      return;
    }

    // 验证日志级别
    if (logger.level !== undefined) {
      const validLevels = ['debug', 'info', 'warn', 'error', 'silent'];
      if (!validLevels.includes(logger.level)) {
        this.errors.push(
          `日志级别 (logger.level) 必须是以下值之一: ${validLevels.join(', ')}`
        );
      }
    }

    // 验证布尔值配置
    const booleanFields = ['color', 'timestamp', 'showLevel'];
    booleanFields.forEach(field => {
      if (logger[field] !== undefined && typeof logger[field] !== 'boolean') {
        this.errors.push(`日志配置 (logger.${field}) 必须是布尔值`);
      }
    });

    // 验证自定义 logger
    if (logger.custom !== undefined) {
      if (typeof logger.custom !== 'object' || logger.custom === null) {
        this.errors.push('自定义 logger (logger.custom) 必须是对象');
      } else {
        const requiredMethods = ['debug', 'info', 'warn', 'error', 'success'];
        requiredMethods.forEach(method => {
          if (typeof logger.custom[method] !== 'function') {
            this.errors.push(`自定义 logger 必须实现 ${method} 方法`);
          }
        });
      }
    }

    // 验证文件配置
    if (logger.file !== undefined) {
      if (typeof logger.file !== 'object' || logger.file === null) {
        this.errors.push('文件配置 (logger.file) 必须是对象');
      } else {
        // 验证文件配置的布尔值
        const fileBooleanFields = ['enabled', 'separateByLevel'];
        fileBooleanFields.forEach(field => {
          if (
            logger.file[field] !== undefined &&
            typeof logger.file[field] !== 'boolean'
          ) {
            this.errors.push(`文件配置 (logger.file.${field}) 必须是布尔值`);
          }
        });

        // 验证文件路径
        if (
          logger.file.path !== undefined &&
          typeof logger.file.path !== 'string'
        ) {
          this.errors.push('文件路径 (logger.file.path) 必须是字符串');
        }

        // 验证文件目录
        if (
          logger.file.dir !== undefined &&
          typeof logger.file.dir !== 'string'
        ) {
          this.errors.push('文件目录 (logger.file.dir) 必须是字符串');
        }

        // 验证文件大小
        if (logger.file.maxSize !== undefined) {
          if (
            typeof logger.file.maxSize !== 'number' ||
            logger.file.maxSize <= 0
          ) {
            this.errors.push('文件最大大小 (logger.file.maxSize) 必须是正数');
          }
        }

        // 验证文件数量
        if (logger.file.maxFiles !== undefined) {
          if (
            typeof logger.file.maxFiles !== 'number' ||
            logger.file.maxFiles <= 0
          ) {
            this.errors.push(
              '最大文件数量 (logger.file.maxFiles) 必须是正整数'
            );
          }
        }
      }
    }
  }

  /**
   * 重置验证状态
   */
  private reset(): void {
    this.errors = [];
    this.warnings = [];
    this.suggestions = [];
  }
}

/**
 * 快速验证配置
 */
export function quickValidate(config: SynthiaConfig): boolean {
  const validator = new ConfigValidator();
  const result = validator.validate(config);
  return result.valid;
}
